import axios from 'axios';
import tokenManager from '../utils/tokenManager';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

console.log('API Base URL:', API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests with validation
api.interceptors.request.use(
  async (config) => {
    console.log('Making API request to:', config.url);
    
    // Get valid token from token manager
    const token = tokenManager.getToken();
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Token present and valid');
    } else {
      console.log('No valid token available');
      // Don't block requests without token, let backend handle auth
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle auth errors and token expiration
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.config.url, response.status);
    return response;
  },
  (error) => {
    console.error('API Error:', error.config?.url, error.response?.status, error.message);
    
    if (error.response?.status === 401) {
      // Token is invalid or expired
      tokenManager.clearToken();
      
      // Check if error message indicates token expiration
      const errorMessage = error.response?.data?.detail || '';
      if (errorMessage.includes('expired')) {
        toast.error('Your session has expired. Please sign in again.');
      } else {
        toast.error('Authentication failed. Please sign in again.');
      }
      
      // Reload after a short delay to show the message
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
    
    return Promise.reject(error);
  }
);

// Events API
export const eventsAPI = {
  getAll: () => api.get('/events'),
  getById: (id) => api.get(`/events/${id}`),
  create: (event) => api.post('/events', event),
  update: (id, event) => api.put(`/events/${id}`, event),
  delete: (id) => api.delete(`/events/${id}`),
};

// Effects API
export const effectsAPI = {
  getAll: () => api.get('/effects'),
  getById: (id) => api.get(`/effects/${id}`),
  create: (effect) => api.post('/effects', effect),
  update: (id, effect) => api.put(`/effects/${id}`, effect),
  delete: (id) => api.delete(`/effects/${id}`),
};

// Placed Effects API
export const placedEffectsAPI = {
  getForEvent: (eventId) => api.get(`/events/${eventId}/effects`),
  create: (eventId, placedEffect) => api.post(`/events/${eventId}/effects`, placedEffect),
  delete: (id) => api.delete(`/placed-effects/${id}`),
};

// Risk Analysis API
export const riskAnalysisAPI = {
  getForEvent: (eventId) => api.get(`/events/${eventId}/risk-analysis`),
  create: (riskAnalysis) => api.post('/risk-analysis', riskAnalysis),
  update: (id, riskAnalysis) => api.put(`/risk-analysis/${id}`, riskAnalysis),
};

// Risk Entries API
export const riskEntriesAPI = {
  getForEvent: (eventId) => api.get(`/events/${eventId}/risk-entries`),
  create: (riskEntry) => api.post('/risk-entries', riskEntry),
  update: (id, riskEntry) => api.put(`/risk-entries/${id}`, riskEntry),
  delete: (id) => api.delete(`/risk-entries/${id}`),
  generateForEvent: (eventId, effectIds) => api.post(`/events/${eventId}/generate-risk-entries`, effectIds),
};

// PDF Generation API
export const pdfAPI = {
  generateRiskAnalysisPDF: (eventId) => {
    return api.get(`/events/${eventId}/generate-risk-analysis-pdf`, {
      responseType: 'blob'
    });
  },
  generateEffectsListPDF: (eventId) => {
    return api.get(`/events/${eventId}/generate-effects-list-pdf`, {
      responseType: 'blob'
    });
  },
};

// Upload API
export const uploadAPI = {
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Documents API
export const documentsAPI = {
  getEventDocuments: (eventId) => api.get(`/events/${eventId}/documents`),
  uploadDocument: (eventId, formData) => {
    return api.post(`/events/${eventId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  downloadDocument: (documentId) => {
    return api.get(`/documents/${documentId}`, {
      responseType: 'blob'
    });
  },
  deleteDocument: (documentId) => api.delete(`/documents/${documentId}`),
};

// Agreements API
api.getAgreements = () => api.get('/agreements');
api.createAgreement = (agreement) => api.post('/agreements', agreement);
api.updateAgreement = (id, agreement) => api.put(`/agreements/${id}`, agreement);
api.deleteAgreement = (id) => api.delete(`/agreements/${id}`);
api.exportAgreement = (id, format) => api.get(`/agreements/${id}/export`, {
  params: { format },
  responseType: 'blob'
});
api.sendAgreement = (id, email) => api.post(`/agreements/${id}/send`, { email });

export { api };
export default api;